//
//  useDateRange.ts
//
//  isana.net,inc. Proprietary and Confidential
//

import { useState, useMemo, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { localStorageManager } from "../utils/localStorage";

const LOCAL_STORAGE_KEY_START = localStorageManager.DATE_RANGE_START;
const LOCAL_STORAGE_KEY_END = localStorageManager.DATE_RANGE_END;

const useDateRange = (initialStart: Dayjs | null, initialEnd: Dayjs | null) => {
  const getInitialDate = (key: string, fallback: Dayjs | null) => {
    const storedValue = localStorage.getItem(key);
    return storedValue ? dayjs(storedValue) : fallback;
  };

  const [dateRange, setDateRange] = useState<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }>({
    startDate: getInitialDate(LOCAL_STORAGE_KEY_START, initialStart),
    endDate: getInitialDate(LOCAL_STORAGE_KEY_END, initialEnd),
  });

  useEffect(() => {
    if (dateRange.startDate?.isValid()) {
      localStorage.setItem(LOCAL_STORAGE_KEY_START, dateRange.startDate.toISOString());
    }
    if (dateRange.endDate?.isValid()) {
      localStorage.setItem(LOCAL_STORAGE_KEY_END, dateRange.endDate.toISOString());
    }
  }, [dateRange]);

  const setDate = (key: "startDate" | "endDate", value: Dayjs | null) => {
    setDateRange((prev) => ({ ...prev, [key]: value }));
  };

  const setStartDate = (newStartDate: Dayjs | null) => {
    setDate("startDate", newStartDate);
  };

  const setEndDate = (newEndDate: Dayjs | null) => {
    setDate("endDate", newEndDate);
  };

  const errorMsg = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return "開始日と終了日は必須です";
    if (dateRange.endDate.isBefore(dateRange.startDate, "day"))
      return "終了日は開始日以降を選択してください";
    if (dateRange.endDate.diff(dateRange.startDate, "day") > 14)
      return "選択できる範囲は最大14日間です";
    return "";
  }, [dateRange]);

  return {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    setStartDate,
    setEndDate,
    errorMsg
  };
};

export default useDateRange;